// URL ↔ filter projection for /rules (ui-spec §10.6). Pure transformations
// so the validator is unit-testable and so the same schema can power both
// TanStack Router's search-param validator and the @observatory/ui-query
// `useRulesList` filter argument.

import {
  isRuleSource,
  isRuleStatus,
  isRuleType,
  type RuleSource,
  type RuleStatus,
  type RuleType,
} from '@observatory/ui-query';

export interface RulesSearch {
  readonly status?: RuleStatus;
  readonly type?: RuleType;
  readonly source?: RuleSource;
  readonly selected?: string;
}

// TanStack Router's `validateSearch` receives the raw search-record. We
// drop anything that doesn't pass the runtime guard — URL tampering can
// otherwise feed unknown enum values into the PostgREST query.
export function parseRulesSearch(raw: Record<string, unknown>): RulesSearch {
  const out: { status?: RuleStatus; type?: RuleType; source?: RuleSource; selected?: string } = {};
  if (isRuleStatus(raw.status)) out.status = raw.status;
  if (isRuleType(raw.type)) out.type = raw.type;
  if (isRuleSource(raw.source)) out.source = raw.source;
  if (typeof raw.selected === 'string') {
    const trimmed = raw.selected.trim();
    // `rules.id` is a UUID column. A tampered `?selected=not-a-uuid` would
    // make every detail query throw "invalid input syntax for type uuid",
    // so reject anything that isn't UUID-shaped here — matches the
    // tamper-tolerant behavior for enum filters above.
    if (trimmed.length > 0 && UUID_RE.test(trimmed)) out.selected = trimmed;
  }
  return out;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
